import { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import api from '../api/client'
import { Badge, getStatusBadge, Avatar, Loading, RatingDisplay, RatingInput } from '../components/ui'
import { ProductDetailSEO } from '../hooks/useSEO'
import { useAuthStore } from '../stores/authStore'
import type { Product, CustomerProductReview } from '../types'
import { getPlatformLabel, getCategoryLabel } from '../utils/productLabels'

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [product, setProduct] = useState<Product | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [purchaseError, setPurchaseError] = useState('')

  // Customer reviews state
  const [reviews, setReviews] = useState<CustomerProductReview[]>([])
  const [reviewsPage, setReviewsPage] = useState(1)
  const [reviewsTotalPages, setReviewsTotalPages] = useState(1)
  const [isLoadingReviews, setIsLoadingReviews] = useState(false)

  // Review form state
  const [reviewRating, setReviewRating] = useState(5)
  const [reviewComment, setReviewComment] = useState('')
  const [isSubmittingReview, setIsSubmittingReview] = useState(false)
  const [reviewError, setReviewError] = useState('')
  const [isDeletingReview, setIsDeletingReview] = useState(false)

  const fetchReviews = useCallback(async (page = 1) => {
    if (!id) return
    setIsLoadingReviews(true)
    try {
      const data = await api.get<{ reviews: CustomerProductReview[]; totalPages: number }>(`/products/${id}/reviews?page=${page}&limit=10`)
      setReviews(data.reviews)
      setReviewsTotalPages(data.totalPages)
      setReviewsPage(page)
    } catch {
      // Silently fail - reviews are optional
    } finally {
      setIsLoadingReviews(false)
    }
  }, [id])

  useEffect(() => {
    fetchProduct()
  }, [id])

  useEffect(() => {
    if (product) {
      fetchReviews(1)
    }
  }, [product?.id, fetchReviews])

  const fetchProduct = async () => {
    setIsLoading(true)
    try {
      const data = await api.get<Product>(`/products/${id}`)
      setProduct(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    if (!product) return

    setPurchaseError('')
    setIsPurchasing(true)

    try {
      const result = await api.post<{ success: boolean; content: string }>(`/products/${id}/purchase`)
      if (result.success) {
        // Refresh product to show content
        await fetchProduct()
      }
    } catch (err) {
      setPurchaseError(err instanceof Error ? err.message : 'Failed to purchase')
    } finally {
      setIsPurchasing(false)
    }
  }

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!product) return

    setReviewError('')
    setIsSubmittingReview(true)

    try {
      await api.post(`/products/${product.id}/reviews`, {
        rating: reviewRating,
        comment: reviewComment.trim() || undefined,
      })
      // Refresh product to get updated rating and userReview
      await fetchProduct()
      await fetchReviews(1)
      setReviewComment('')
      setReviewRating(5)
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'Failed to submit review')
    } finally {
      setIsSubmittingReview(false)
    }
  }

  const handleDeleteReview = async () => {
    if (!product) return

    setIsDeletingReview(true)
    try {
      await api.delete(`/products/${product.id}/reviews`)
      // Refresh product to get updated rating and remove userReview
      await fetchProduct()
      await fetchReviews(1)
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'Failed to delete review')
    } finally {
      setIsDeletingReview(false)
    }
  }

  if (isLoading) {
    return <Loading message="Loading product..." />
  }

  if (error || !product) {
    return (
      <div>
        <div style={{ marginBottom: '1.5rem' }}>
          <Link to="/products" className="text-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Marketplace
          </Link>
        </div>
        <div className="alert alert-error">{error || 'Product not found'}</div>
      </div>
    )
  }

  const statusBadge = getStatusBadge(product.status)
  const isOwner = user?.id === product.sellerId
  const canViewContent = isOwner || product.hasPurchased
  const canPurchase = !isOwner && !product.hasPurchased && product.status === 'APPROVED'

  return (
    <div>
      <ProductDetailSEO
        title={product.title}
        description={product.description}
        price={product.price}
        productId={product.id}
        sellerName={product.seller.name}
        averageRating={product.averageRating}
        totalReviews={product.totalReviews}
        category={getCategoryLabel(product.category)}
      />
      <div style={{ marginBottom: '1.5rem' }}>
        <Link to="/products" className="text-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Marketplace
        </Link>
      </div>

      <div className="card-static">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Badge status={statusBadge.variant}>{statusBadge.label}</Badge>
            <Badge variant="blue">{getCategoryLabel(product.category)}</Badge>
            <Badge variant="gray">{getPlatformLabel(product.platform)}</Badge>
          </div>
          <span className="usdc-amount">${product.price} USDC</span>
        </div>

        <h1 style={{ marginBottom: '0.5rem' }}>{product.title}</h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <Avatar name={product.seller.name} size="sm" />
          <span className="text-secondary">Listed by {product.seller.name}</span>
          {product.totalSales > 0 && (
            <span className="text-secondary">
              {product.totalSales} sale{product.totalSales !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Description</h3>
          <p className="text-secondary" style={{ lineHeight: 1.6 }}>{product.description}</p>
        </div>

        {product.tags.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '0.5rem' }}>Tags</h3>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {product.tags.map((tag) => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
          </div>
        )}

        {purchaseError && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{purchaseError}</div>}

        {canPurchase && (
          <div style={{ marginBottom: '1.5rem' }}>
            <button
              className="btn btn-primary"
              onClick={handlePurchase}
              disabled={isPurchasing}
            >
              {isPurchasing ? 'Purchasing...' : `Purchase for $${product.price} USDC`}
            </button>
          </div>
        )}

        {product.hasPurchased && !isOwner && (
          <div className="alert alert-success" style={{ marginBottom: '1.5rem' }}>
            You own this product.
          </div>
        )}

        {isOwner && (
          <div className="alert" style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)' }}>
            This is your product.
          </div>
        )}

        {/* Content section */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Product Content</h3>
          {canViewContent && product.content ? (
            <div style={{
              background: 'var(--bg-secondary)',
              padding: '1rem',
              borderRadius: 'var(--radius)',
              fontFamily: 'monospace',
              fontSize: '14px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '500px',
              overflow: 'auto',
            }}>
              {product.content}
            </div>
          ) : (
            <div style={{
              background: 'var(--bg-secondary)',
              padding: '2rem',
              borderRadius: 'var(--radius)',
              textAlign: 'center',
            }}>
              <p className="text-secondary">
                {product.previewSnippet ? (
                  <>
                    <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                      {product.previewSnippet}...
                    </span>
                    <br /><br />
                    <em>Purchase to view full content</em>
                  </>
                ) : (
                  'Purchase this product to view its full content.'
                )}
              </p>
            </div>
          )}
        </div>

        {/* Review status for owner */}
        {isOwner && product.reviews && product.reviews.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Review Status</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {product.reviews.map((review, index) => (
                <div
                  key={review.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.75rem',
                    background: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  <span style={{ fontWeight: 500 }}>Review {index + 1}</span>
                  <Badge status={getStatusBadge(review.decision as 'PENDING' | 'APPROVED' | 'REJECTED').variant}>
                    {review.decision}
                  </Badge>
                  {review.reason && (
                    <span className="text-secondary" style={{ fontSize: '14px' }}>{review.reason}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Customer Reviews Section */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>Customer Reviews</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {product.totalReviews > 0 ? (
                <>
                  <RatingDisplay value={product.averageRating} size="md" />
                  <span className="text-secondary">({product.totalReviews} review{product.totalReviews !== 1 ? 's' : ''})</span>
                </>
              ) : (
                <span className="text-secondary">No reviews yet</span>
              )}
            </div>
          </div>

          {/* User's own review */}
          {product.userReview && (
            <div style={{
              background: 'var(--bg-secondary)',
              padding: '1rem',
              borderRadius: 'var(--radius)',
              marginBottom: '1rem',
              border: '1px solid var(--primary)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Avatar name={product.userReview.buyer.name} size="sm" />
                  <div>
                    <div style={{ fontWeight: 500 }}>{product.userReview.buyer.name} (You)</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {new Date(product.userReview.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <RatingDisplay value={product.userReview.rating} size="sm" showValue={false} />
                  <button
                    className="btn btn-secondary"
                    onClick={handleDeleteReview}
                    disabled={isDeletingReview}
                    style={{ padding: '0.25rem 0.5rem', fontSize: '12px' }}
                  >
                    {isDeletingReview ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
              {product.userReview.comment && (
                <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>{product.userReview.comment}</p>
              )}
            </div>
          )}

          {/* Review form for purchasers who haven't reviewed */}
          {product.hasPurchased && !isOwner && !product.userReview && (
            <form onSubmit={handleSubmitReview} style={{
              background: 'var(--bg-secondary)',
              padding: '1rem',
              borderRadius: 'var(--radius)',
              marginBottom: '1rem',
            }}>
              <h4 style={{ marginBottom: '0.75rem' }}>Write a Review</h4>
              {reviewError && (
                <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>{reviewError}</div>
              )}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '14px' }}>Rating</label>
                <RatingInput value={reviewRating} onChange={setReviewRating} size="lg" />
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '14px' }}>Comment (optional)</label>
                <textarea
                  className="form-input"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Share your experience with this product..."
                  rows={3}
                  maxLength={1000}
                  style={{ width: '100%', resize: 'vertical' }}
                />
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                  {reviewComment.length}/1000
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmittingReview}
              >
                {isSubmittingReview ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          )}

          {/* Reviews list */}
          {isLoadingReviews ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <Loading message="Loading reviews..." />
            </div>
          ) : reviews.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {reviews.filter(r => r.buyerId !== user?.id).map((review) => (
                <div
                  key={review.id}
                  style={{
                    background: 'var(--bg-secondary)',
                    padding: '1rem',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Avatar name={review.buyer.name} size="sm" />
                      <div>
                        <div style={{ fontWeight: 500 }}>{review.buyer.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {new Date(review.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <RatingDisplay value={review.rating} size="sm" showValue={false} />
                  </div>
                  {review.comment && (
                    <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>{review.comment}</p>
                  )}
                </div>
              ))}

              {/* Pagination */}
              {reviewsTotalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => fetchReviews(reviewsPage - 1)}
                    disabled={reviewsPage <= 1 || isLoadingReviews}
                  >
                    Previous
                  </button>
                  <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.75rem' }}>
                    Page {reviewsPage} of {reviewsTotalPages}
                  </span>
                  <button
                    className="btn btn-secondary"
                    onClick={() => fetchReviews(reviewsPage + 1)}
                    disabled={reviewsPage >= reviewsTotalPages || isLoadingReviews}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          ) : !product.userReview ? (
            <div style={{
              background: 'var(--bg-secondary)',
              padding: '2rem',
              borderRadius: 'var(--radius)',
              textAlign: 'center',
            }}>
              <p className="text-secondary">No reviews yet. Be the first to review this product!</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
